import { useState, useEffect } from 'react';

interface Assignment {
  id: string;
  title: string;
  description: string;
  course_id: string;
  course_title: string;
  due_date: string;
  max_score: number;
  created_at: string;
  total_submissions: number;
  graded_submissions: number;
}

interface Submission {
  id: string;
  assignment_id: string;
  assignment_title: string;
  student_id: string;
  student_name: string;
  student_email: string;
  content: string;
  submitted_at: string;
  score: number | null;
  feedback: string | null;
  status: 'pending' | 'graded';
  course_title: string;
}

interface AssignmentsProps {
  userProfile: any;
}

export default function Assignments({ userProfile }: AssignmentsProps) {
  const [assignments, setAssignments] = useState<Assignment[]>([]);
  const [submissions, setSubmissions] = useState<Submission[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('assignments');
  const [selectedSubmission, setSelectedSubmission] = useState<Submission | null>(null);
  const [showGradingModal, setShowGradingModal] = useState(false);
  const [gradingForm, setGradingForm] = useState({
    score: '',
    feedback: ''
  });

  useEffect(() => {
    fetchAssignments();
    fetchSubmissions();
  }, [userProfile]);

  const fetchAssignments = async () => {
    try {
      console.log('🔄 Đang tải bài tập cho giáo viên:', userProfile.id);
      
      // Sử dụng Edge Function để lấy bài tập (bypass RLS)
      const url = `${import.meta.env.VITE_PUBLIC_SUPABASE_URL}/functions/v1/get-teacher-assignments?instructor_id=${userProfile.id}`;
      console.log('🌐 URL:', url);

      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY}`,
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        const result = await response.json();
        console.log('✅ Kết quả từ Edge Function:', result);
        
        if (result.success && result.data) {
          console.log('✅ Số lượng bài tập:', result.data.length);
          setAssignments(result.data);
        } else {
          console.error('❌ Edge Function trả về lỗi:', result.error);
          setAssignments([]);
        }
      } else {
        console.error('❌ Lỗi khi gọi Edge Function:', response.status);
        const errorText = await response.text();
        console.error('Chi tiết lỗi:', errorText);
        setAssignments([]);
      }
    } catch (error) {
      console.error('❌ Lỗi khi tải bài tập:', error);
      setAssignments([]);
    }
  };

  const fetchSubmissions = async () => {
    try {
      console.log('🔄 Đang tải bài nộp cho giáo viên:', userProfile.id);
      
      // Sử dụng Edge Function mới để lấy submissions
      const url = `${import.meta.env.VITE_PUBLIC_SUPABASE_URL}/functions/v1/get-teacher-submissions?instructor_id=${userProfile.id}`;
      console.log('🌐 URL:', url);

      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY}`,
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        const result = await response.json();
        console.log('✅ Kết quả submissions từ Edge Function:', result);
        
        if (result.success && result.data) {
          console.log('✅ Số lượng bài nộp:', result.data.length);
          setSubmissions(result.data);
        } else {
          console.error('❌ Edge Function trả về lỗi:', result.error);
          setSubmissions([]);
        }
      } else {
        console.error('❌ Lỗi khi gọi Edge Function:', response.status);
        const errorText = await response.text();
        console.error('Chi tiết lỗi:', errorText);
        setSubmissions([]);
      }
    } catch (error) {
      console.error('❌ Lỗi khi tải bài nộp:', error);
      setSubmissions([]);
    } finally {
      setLoading(false);
    }
  };

  const handleGradeSubmission = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedSubmission) return;

    try {
      console.log('📝 Đang chấm điểm:', {
        submission_id: selectedSubmission.id,
        score: gradingForm.score,
        feedback: gradingForm.feedback
      });

      // Sử dụng Edge Function để chấm điểm (bypass RLS)
      const response = await fetch(`${import.meta.env.VITE_PUBLIC_SUPABASE_URL}/functions/v1/grade-submission`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          submission_id: selectedSubmission.id,
          score: parseInt(gradingForm.score),
          feedback: gradingForm.feedback || null
        })
      });

      const result = await response.json();
      console.log('✅ Kết quả chấm điểm:', result);

      if (response.ok && result.success) {
        alert('✅ Đã chấm điểm thành công!');
        setShowGradingModal(false);
        setGradingForm({ score: '', feedback: '' });
        setSelectedSubmission(null);
        fetchSubmissions(); // Refresh submissions
      } else {
        throw new Error(result.error || 'Không thể chấm điểm');
      }
    } catch (err) {
      console.error('❌ Lỗi khi chấm điểm:', err);
      alert(`❌ Có lỗi xảy ra: ${err instanceof Error ? err.message : 'Vui lòng thử lại'}`);
    }
  };

  const openGradingModal = (submission: Submission) => {
    setSelectedSubmission(submission);
    setGradingForm({
      score: submission.score?.toString() || '',
      feedback: submission.feedback || ''
    });
    setShowGradingModal(true);
  };

  const pendingSubmissions = submissions.filter(s => s.status === 'pending');
  const gradedSubmissions = submissions.filter(s => s.status === 'graded');

  // Calculate submission counts for assignments
  const assignmentsWithCounts = assignments.map(assignment => {
    const assignmentSubmissions = submissions.filter(s => s.assignment_id === assignment.id);
    return {
      ...assignment,
      total_submissions: assignmentSubmissions.length,
      graded_submissions: assignmentSubmissions.filter(s => s.status === 'graded').length
    };
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="flex items-center space-x-2 text-gray-500">
          <i className="ri-loader-4-line animate-spin"></i>
          <span>Đang tải dữ liệu bài tập...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-gray-900">Quản lý bài tập</h1>
        <button
          onClick={() => {
            setLoading(true);
            fetchAssignments();
            fetchSubmissions();
          }}
          className="flex items-center px-4 py-2 text-blue-600 border border-blue-600 rounded-lg hover:bg-blue-50 transition-all whitespace-nowrap"
        >
          <i className="ri-refresh-line mr-2"></i>
          Làm mới
        </button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <div className="flex items-center">
            <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center">
              <i className="ri-file-list-line text-blue-600"></i>
            </div>
            <div className="ml-3">
              <p className="text-sm font-medium text-gray-600">Tổng bài tập</p>
              <p className="text-xl font-bold text-gray-900">{assignments.length}</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <div className="flex items-center">
            <div className="w-8 h-8 bg-yellow-100 rounded-lg flex items-center justify-center">
              <i className="ri-time-line text-yellow-600"></i>
            </div>
            <div className="ml-3">
              <p className="text-sm font-medium text-gray-600">Chờ chấm</p>
              <p className="text-xl font-bold text-gray-900">{pendingSubmissions.length}</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <div className="flex items-center">
            <div className="w-8 h-8 bg-green-100 rounded-lg flex items-center justify-center">
              <i className="ri-check-line text-green-600"></i>
            </div>
            <div className="ml-3">
              <p className="text-sm font-medium text-gray-600">Đã chấm</p>
              <p className="text-xl font-bold text-gray-900">{gradedSubmissions.length}</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <div className="flex items-center">
            <div className="w-8 h-8 bg-purple-100 rounded-lg flex items-center justify-center">
              <i className="ri-file-text-line text-purple-600"></i>
            </div>
            <div className="ml-3">
              <p className="text-sm font-medium text-gray-600">Tổng bài nộp</p>
              <p className="text-xl font-bold text-gray-900">{submissions.length}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="bg-white rounded-xl shadow-sm border">
        <div className="border-b">
          <nav className="flex space-x-8 px-6">
            <button
              onClick={() => setActiveTab('assignments')}
              className={`py-4 px-1 border-b-2 font-medium text-sm whitespace-nowrap ${
                activeTab === 'assignments'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              Danh sách bài tập ({assignments.length})
            </button>
            <button
              onClick={() => setActiveTab('pending')}
              className={`py-4 px-1 border-b-2 font-medium text-sm whitespace-nowrap ${
                activeTab === 'pending'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              Chờ chấm điểm ({pendingSubmissions.length})
            </button>
            <button
              onClick={() => setActiveTab('graded')}
              className={`py-4 px-1 border-b-2 font-medium text-sm whitespace-nowrap ${
                activeTab === 'graded'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              Đã chấm ({gradedSubmissions.length})
            </button>
          </nav>
        </div>

        <div className="p-6">
          {activeTab === 'assignments' && (
            <div className="space-y-4">
              {assignmentsWithCounts.length === 0 ? (
                <div className="text-center py-12">
                  <i className="ri-file-list-line text-4xl text-gray-300 mb-4"></i>
                  <p className="text-gray-500">Chưa có bài tập nào</p>
                  <p className="text-sm text-gray-400 mt-1">Tạo bài tập mới từ trang "Khóa học của tôi"</p>
                </div>
              ) : (
                assignmentsWithCounts.map((assignment) => (
                  <div key={assignment.id} className="border rounded-lg p-6 hover:bg-gray-50 transition-colors">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <h3 className="text-lg font-medium text-gray-900 mb-2">{assignment.title}</h3>
                        <p className="text-gray-600 mb-3 line-clamp-2">{assignment.description}</p>
                        <div className="flex items-center space-x-6 text-sm text-gray-500">
                          <span className="flex items-center">
                            <i className="ri-book-line mr-1"></i>
                            {assignment.course_title}
                          </span>
                          <span className="flex items-center">
                            <i className="ri-calendar-line mr-1"></i>
                            Hạn: {new Date(assignment.due_date).toLocaleDateString('vi-VN')}
                          </span>
                          <span className="flex items-center">
                            <i className="ri-file-list-line mr-1"></i>
                            {assignment.graded_submissions}/{assignment.total_submissions} đã chấm
                          </span>
                          <span className="flex items-center">
                            <i className="ri-star-line mr-1"></i>
                            {assignment.max_score} điểm
                          </span>
                        </div>
                      </div>
                      <div className="flex space-x-2 ml-4">
                        <button className="bg-blue-100 text-blue-700 px-3 py-2 rounded-lg hover:bg-blue-200 transition-colors text-sm whitespace-nowrap">
                          <i className="ri-eye-line mr-1"></i>
                          Chi tiết
                        </button>
                        <button className="bg-green-100 text-green-700 px-3 py-2 rounded-lg hover:bg-green-200 transition-colors text-sm whitespace-nowrap">
                          <i className="ri-download-line mr-1"></i>
                          Tải bài nộp
                        </button>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          )}

          {activeTab === 'pending' && (
            <div className="space-y-4">
              {pendingSubmissions.length === 0 ? (
                <div className="text-center py-12">
                  <i className="ri-time-line text-4xl text-gray-300 mb-4"></i>
                  <p className="text-gray-500">Không có bài nộp nào cần chấm điểm</p>
                </div>
              ) : (
                pendingSubmissions.map((submission) => (
                  <div key={submission.id} className="border rounded-lg p-6 hover:bg-gray-50 transition-colors">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center space-x-3 mb-2">
                          <h3 className="text-lg font-medium text-gray-900">{submission.assignment_title}</h3>
                          <span className="bg-yellow-100 text-yellow-800 px-2 py-1 text-xs font-medium rounded-full">
                            Chờ chấm
                          </span>
                        </div>
                        <div className="flex items-center space-x-6 text-sm text-gray-600 mb-3">
                          <span className="flex items-center">
                            <i className="ri-user-line mr-1"></i>
                            {submission.student_name}
                          </span>
                          <span className="flex items-center">
                            <i className="ri-mail-line mr-1"></i>
                            {submission.student_email}
                          </span>
                          <span className="flex items-center">
                            <i className="ri-calendar-line mr-1"></i>
                            Nộp: {new Date(submission.submitted_at).toLocaleDateString('vi-VN')}
                          </span>
                        </div>
                        <p className="text-gray-600 text-sm">{submission.course_title}</p>
                      </div>
                      <div className="flex space-x-2 ml-4">
                        <button
                          onClick={() => openGradingModal(submission)}
                          className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition-colors text-sm whitespace-nowrap"
                        >
                          <i className="ri-edit-line mr-1"></i>
                          Chấm điểm
                        </button>
                        <button className="bg-blue-100 text-blue-700 px-3 py-2 rounded-lg hover:bg-blue-200 transition-colors text-sm whitespace-nowrap">
                          <i className="ri-eye-line mr-1"></i>
                          Xem bài
                        </button>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          )}

          {activeTab === 'graded' && (
            <div className="space-y-4">
              {gradedSubmissions.length === 0 ? (
                <div className="text-center py-12">
                  <i className="ri-check-line text-4xl text-gray-300 mb-4"></i>
                  <p className="text-gray-500">Chưa có bài nào được chấm điểm</p>
                </div>
              ) : (
                gradedSubmissions.map((submission) => (
                  <div key={submission.id} className="border rounded-lg p-6 hover:bg-gray-50 transition-colors">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center space-x-3 mb-2">
                          <h3 className="text-lg font-medium text-gray-900">{submission.assignment_title}</h3>
                          <span className="bg-green-100 text-green-800 px-2 py-1 text-xs font-medium rounded-full">
                            Đã chấm: {submission.score} điểm
                          </span>
                        </div>
                        <div className="flex items-center space-x-6 text-sm text-gray-600 mb-3">
                          <span className="flex items-center">
                            <i className="ri-user-line mr-1"></i>
                            {submission.student_name}
                          </span>
                          <span className="flex items-center">
                            <i className="ri-mail-line mr-1"></i>
                            {submission.student_email}
                          </span>
                          <span className="flex items-center">
                            <i className="ri-calendar-line mr-1"></i>
                            Nộp: {new Date(submission.submitted_at).toLocaleDateString('vi-VN')}
                          </span>
                        </div>
                        <p className="text-gray-600 text-sm mb-2">{submission.course_title}</p>
                        {submission.feedback && (
                          <div className="bg-blue-50 p-3 rounded-lg">
                            <p className="text-sm text-blue-800">
                              <strong>Nhận xét:</strong> {submission.feedback}
                            </p>
                          </div>
                        )}
                      </div>
                      <div className="flex space-x-2 ml-4">
                        <button
                          onClick={() => openGradingModal(submission)}
                          className="bg-orange-100 text-orange-700 px-3 py-2 rounded-lg hover:bg-orange-200 transition-colors text-sm whitespace-nowrap"
                        >
                          <i className="ri-edit-line mr-1"></i>
                          Sửa điểm
                        </button>
                        <button className="bg-blue-100 text-blue-700 px-3 py-2 rounded-lg hover:bg-blue-200 transition-colors text-sm whitespace-nowrap">
                          <i className="ri-eye-line mr-1"></i>
                          Xem bài
                        </button>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          )}
        </div>
      </div>

      {/* Grading Modal */}
      {showGradingModal && selectedSubmission && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b">
              <div className="flex items-center justify-between">
                <h2 className="text-xl font-semibold text-gray-900">
                  Chấm điểm bài tập
                </h2>
                <button
                  onClick={() => setShowGradingModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <i className="ri-close-line text-xl"></i>
                </button>
              </div>
            </div>

            <div className="p-6">
              <div className="mb-6">
                <h3 className="text-lg font-medium text-gray-900 mb-2">{selectedSubmission.assignment_title}</h3>
                <div className="bg-gray-50 p-4 rounded-lg mb-4">
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <span className="text-gray-600">Học sinh:</span>
                      <div className="font-medium">{selectedSubmission.student_name}</div>
                    </div>
                    <div>
                      <span className="text-gray-600">Email:</span>
                      <div className="font-medium">{selectedSubmission.student_email}</div>
                    </div>
                    <div>
                      <span className="text-gray-600">Khóa học:</span>
                      <div className="font-medium">{selectedSubmission.course_title}</div>
                    </div>
                    <div>
                      <span className="text-gray-600">Ngày nộp:</span>
                      <div className="font-medium">{new Date(selectedSubmission.submitted_at).toLocaleDateString('vi-VN')}</div>
                    </div>
                  </div>
                </div>
                
                <div className="mb-4">
                  <h4 className="font-medium text-gray-900 mb-2">Nội dung bài làm:</h4>
                  <div className="bg-gray-50 p-4 rounded-lg max-h-40 overflow-y-auto">
                    <p className="text-gray-700 whitespace-pre-wrap">{selectedSubmission.content}</p>
                  </div>
                </div>
              </div>

              <form onSubmit={handleGradeSubmission} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Điểm số *
                  </label>
                  <input
                    type="number"
                    min="0"
                    max="100"
                    required
                    value={gradingForm.score}
                    onChange={(e) => setGradingForm({...gradingForm, score: e.target.value})}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Nhập điểm (0-100)"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Nhận xét
                  </label>
                  <textarea
                    rows={4}
                    value={gradingForm.feedback}
                    onChange={(e) => setGradingForm({...gradingForm, feedback: e.target.value})}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Nhận xét về bài làm của học sinh..."
                    maxLength={500}
                  ></textarea>
                </div>

                <div className="flex space-x-3 pt-4">
                  <button
                    type="button"
                    onClick={() => setShowGradingModal(false)}
                    className="flex-1 bg-gray-100 text-gray-700 py-3 rounded-lg hover:bg-gray-200 transition-colors whitespace-nowrap"
                  >
                    Hủy
                  </button>
                  <button
                    type="submit"
                    className="flex-1 bg-blue-600 text-white py-3 rounded-lg hover:bg-blue-700 transition-colors whitespace-nowrap"
                  >
                    <i className="ri-save-line mr-2"></i>
                    Lưu điểm
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}